import { ACCOUNT_SETTINGS_NOTIFICATIONS_ID } from '@va/constants';
import { getSupportCenterDomain } from '@va/dashboard/selectors/customization';
import { SettingsHeading } from '@va/dashboard/ui/pages';
import { GlobeIcon, PencilEditIcon } from '@va/icons';
import { useTranslate } from '@va/localization';
import { contributorRole } from '@va/standalone/shared/selectors';
import { Button, IconWithBackground, InputWithSubsection } from '@va/ui/design-system';
import { is3asApp, isWixApp } from '@va/util/helpers';
import Skeleton from 'react-loading-skeleton';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import { EmailListItems } from '../components';
import { EmailReportsToggle } from './EmailReportsToggle';
import useNotificationPreferences from './useNotificationPreferences';

export const NotificationPreferences = () => {
  const translate = useTranslate();
  const supportCenterDomain = useSelector(getSupportCenterDomain);
  const {
    emailAddress,
    onChangeEmail,
    allEmails,
    formError,
    isLoadingGettingNotificationSettings,
    isNotificationSettingsUpdateSucceeded,
    isSaveEmailButtonDisabled,
    onSaveEmail,
    onRemoveEmail,
    onResendEmailConfirmation,
  } = useNotificationPreferences();

  const isContributor = useSelector(contributorRole);

  const cardHeading = (
    <SettingsHeading
      title={translate('form.emailReports.title')}
      subText={translate('form.emailReports.desc')}
      tooltip={translate('card.notifications.tooltip', { supportCenterDomain })}
    />
  );

  if (isLoadingGettingNotificationSettings) {
    return (
      <div className='gap-18px'>
        {cardHeading}
        <div className='flex flex-col gap-2'>
          <Skeleton className='h-72 rounded-18' />
          <Skeleton className='h-72 rounded-18' />
          <Skeleton className='h-72 rounded-18' />
          <Skeleton className='h-72 rounded-18' />
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-18px'>
      {cardHeading}
      <div className='flex flex-col gap-3'>
        <InputWithSubsection
          disabled={isContributor}
          infoTooltip={isContributor ? translate('tooltips.contributorRole') : undefined}
          isLoading={isSaveEmailButtonDisabled}
          onSubmit={onSaveEmail}
          icon={
            <IconWithBackground className='!bg-white' icon={() => <GlobeIcon className='w-6 h-6' color='#696969' />} />
          }
          successSubmit={isNotificationSettingsUpdateSucceeded}
          inputProps={{
            value: emailAddress,
            onChange: onChangeEmail,
            placeholder: translate('card.emailReports.addEmail.placeholder'),
            name: 'emailAddressInput',
            label: translate('card.emailReports.addEmail.label'),
          }}
          error={formError}
          listItemWrapperClass='!grid-cols-1'
          onRemove={() => {}}
          listItems={[]}
          itemsLabel={''}
        >
          <EmailListItems
            isLoading={isLoadingGettingNotificationSettings}
            listItems={allEmails ?? []}
            onRemoveClick={onRemoveEmail}
            onResendEmailConfirmation={onResendEmailConfirmation}
          />
        </InputWithSubsection>

        <EmailReportsToggle />
      </div>
      <RedirectToAccountSettingsBtn />
    </div>
  );
};

const RedirectToAccountSettingsBtn = () => {
  const translate = useTranslate();
  const history = useHistory();

  if (is3asApp() || isWixApp()) return null;

  return (
    <Button
      icon={() => <PencilEditIcon color='#969696' />}
      text={translate('button.edit.notification.preferences')}
      className='w-full flex justify-center'
      onClick={() => history.push(`/my-account/info#${ACCOUNT_SETTINGS_NOTIFICATIONS_ID}`)}
      color='tertiary'
    />
  );
};
